import * as moment from "moment";
import { ConvertedCostRevenue } from "../../types";
import { totalNonRecoverable, totalRecoverable } from "../../utils";
import { EffectiveRentUtil } from "../headline-rent";
import { basisMultiplier, miniFs, rounddown4 } from "./utils";
import { ModelAssumptionsUtil } from "./model-assumptions-util";
import { RentReviewDetailType, RentReviewTypes } from "src/app/core/v2/types";

export function calculateLeaseholdUnderMRDualRateWithASFAndAllowanceForTaxDouble(
  costRevenue: ConvertedCostRevenue,
  subCostRevenue: ConvertedCostRevenue,
  valuationDate: Date,
  {
    activeBreakOffOption, 
    allRiskYield, 
    annualSinkingFund, 
    taxRate, 
    timingId,
    subActiveBreakOffOption,
    marketRent,
    subGrowthRate,
    subEstimatedVoidPeriod,
    annualSinkingFund2ndTerm
  }: {
    activeBreakOffOption: boolean,
    allRiskYield: number,
    annualSinkingFund: number,
    annualSinkingFund2ndTerm: number,
    taxRate: number,
    timingId: number,
    subActiveBreakOffOption: boolean,
    marketRent: number,
    subGrowthRate: number,
    subEstimatedVoidPeriod: number
  }
) {
  const _valuationDate = valuationDate ? moment(valuationDate) : null
  const unexpiredLeaseTerm = _valuationDate ? ModelAssumptionsUtil.unexpiredLeaseTerm(costRevenue, _valuationDate) : null
  const yearsToReview = _valuationDate ? ModelAssumptionsUtil.yearsToReview(costRevenue, _valuationDate) : null
  const yearsToWriteOff = _valuationDate ? ModelAssumptionsUtil.yearsToWriteOff(costRevenue, _valuationDate) : null
  const yearsToBreakOff = _valuationDate ? ModelAssumptionsUtil.yearsToBreakOff(costRevenue, _valuationDate) : null

  const subUnexpiredLeaseTerm = _valuationDate && subCostRevenue ? ModelAssumptionsUtil.unexpiredLeaseTerm(subCostRevenue, _valuationDate) : null
  const subYearsToReview = _valuationDate && subCostRevenue ? ModelAssumptionsUtil.yearsToReview(subCostRevenue, _valuationDate) : null
  const subYearsToWriteOff = _valuationDate && subCostRevenue ? ModelAssumptionsUtil.yearsToWriteOff(subCostRevenue, _valuationDate) : null
  const subYearsToBreakOff = _valuationDate && subCostRevenue ? ModelAssumptionsUtil.yearsToBreakOff(subCostRevenue, _valuationDate) : null

  const _basisMultiplier = basisMultiplier(costRevenue.rentBasis)
  const headEffectiveRent = EffectiveRentUtil.getValueFromConvertedCostRevenue(costRevenue)
  const headTotalRecoverable = totalRecoverable(costRevenue) * _basisMultiplier
  const headTotalNonRecoverable = totalNonRecoverable(costRevenue) * _basisMultiplier
  const passingRent = headEffectiveRent + headTotalRecoverable - headTotalNonRecoverable

  const _subBasisMultiplier = subCostRevenue ? basisMultiplier(subCostRevenue.rentBasis) : undefined
  const subEffectiveRent = subCostRevenue ? EffectiveRentUtil.getValueFromConvertedCostRevenue(subCostRevenue) : undefined
  const subTotalRecoverable = subCostRevenue ? totalRecoverable(subCostRevenue) * _subBasisMultiplier : undefined
  const subTotalNonRecoverable = subCostRevenue ? totalNonRecoverable(subCostRevenue) * _subBasisMultiplier : undefined
  const profitRent = subEffectiveRent + subTotalRecoverable - subTotalNonRecoverable - passingRent

  const subMinYear = miniFs(
    subActiveBreakOffOption
      ? {'Years to Review': subYearsToReview, 'Years to Write off': subYearsToWriteOff, 'Years to Break off': subYearsToBreakOff}
      : {'Years to Review': subYearsToReview, 'Years to Write off': subYearsToWriteOff}
  )

  const subLeaseYPUntilReview = yieldPurchase(
    allRiskYield,
    annualSinkingFund,
    taxRate,
    subMinYear.value,
    timingId
  ) 
  const capitalValueOfProfitRentOf1stTerm = profitRent * subLeaseYPUntilReview
  const _subLeaseReversion = subCostRevenue
    ? subLeaseReversion(
        marketRent,
        subEffectiveRent,
        subGrowthRate,
        subActiveBreakOffOption,
        subEstimatedVoidPeriod,
        subMinYear,
        subUnexpiredLeaseTerm,
        subYearsToBreakOff,
        subCostRevenue.rentReviewType ? subCostRevenue.rentReviewType.id : undefined,
        subCostRevenue.rentReviewDetailType,
        subCostRevenue.rentReviewDetail
      )
    : {value: undefined}

  const _profitRent2ndTerm = subCostRevenue 
    ? profitRent2ndTerm(
      subCostRevenue.rentReviewType ? subCostRevenue.rentReviewType.id : undefined,
      _subLeaseReversion.value,
      passingRent,
      subTotalRecoverable,
      subTotalNonRecoverable,
      subEffectiveRent
    )
    : {value: undefined}

  const minYearWithUE = miniFs(
    activeBreakOffOption
      ? {'Head Lease Unexpired Lease term': unexpiredLeaseTerm, 'Head Lease Years to Review': yearsToReview, 'Head Lease Years to Write off': yearsToWriteOff, 'Head Lease Years to Break off': yearsToBreakOff}
      : {'Head Lease Unexpired Lease term': unexpiredLeaseTerm, 'Head Lease Years to Review': yearsToReview, 'Head Lease Years to Write off': yearsToWriteOff}
  )
  const subMinYearWithUE = miniFs(
    subActiveBreakOffOption
      ? {'Sub Lease Unexpired Lease term': subUnexpiredLeaseTerm, 'Sub Lease Years to Review': subYearsToReview, 'Sub Lease Years to Write off': subYearsToWriteOff, 'Sub Lease Years to Break off': subYearsToBreakOff}
      : {'Sub Lease Unexpired Lease term': subUnexpiredLeaseTerm,'Sub Lease Years to Review': subYearsToReview, 'Sub Lease Years to Write off': subYearsToWriteOff}
  )
  const yp2ndTerm = yieldPurchase2ndTerm(
    allRiskYield,
    annualSinkingFund2ndTerm,
    minYearWithUE.value,
    subMinYearWithUE.value,
    taxRate,
    timingId
  )

  const _presentValue = presentValue(
    allRiskYield,
    subActiveBreakOffOption,
    subYearsToBreakOff,
    subEstimatedVoidPeriod,
    subMinYearWithUE,
    subUnexpiredLeaseTerm
  )

  const capitalValueOfProfitRentOf2ndTerm = _profitRent2ndTerm.value * yp2ndTerm * _presentValue.value
  const value = capitalValueOfProfitRentOf1stTerm + capitalValueOfProfitRentOf2ndTerm

  return {
    headEffectiveRent,
    basisMultiplier: _basisMultiplier,
    headRecoverable: {
      total: headTotalRecoverable,
      recoverablePropertyManagement: costRevenue.recoverablePropertyManagement * _basisMultiplier,
      recoverableLeasingExpenses: costRevenue.recoverableLeasingExpenses * _basisMultiplier, 
      recoverableUtilities: costRevenue.recoverableUtilities * _basisMultiplier,
      recoverableMaintenance: costRevenue.recoverableMaintenance * _basisMultiplier,
      recoverableInsurance: costRevenue.recoverableInsurance * _basisMultiplier,
      recoverableJanitorial: costRevenue.recoverableJanitorial * _basisMultiplier,
      recoverablePropertyTaxes: costRevenue.recoverablePropertyTaxes * _basisMultiplier,
      recoverableBusiness: costRevenue.recoverableBusiness * _basisMultiplier,
      recoverableOthers: costRevenue.recoverableOthers * _basisMultiplier,
    },
    headNonRecoverable: {
      total: headTotalNonRecoverable,
      nonRecoverablePropertyManagement: costRevenue.nonRecoverablePropertyManagement * _basisMultiplier,
      nonRecoverableLeasingExpenses: costRevenue.nonRecoverableLeasingExpenses * _basisMultiplier, 
      nonRecoverableUtilities: costRevenue.nonRecoverableUtilities * _basisMultiplier,
      nonRecoverableMaintenance: costRevenue.nonRecoverableMaintenance * _basisMultiplier,
      nonRecoverableInsurance: costRevenue.nonRecoverableInsurance * _basisMultiplier,
      nonRecoverableJanitorial: costRevenue.nonRecoverableJanitorial * _basisMultiplier,
      nonRecoverablePropertyTaxes: costRevenue.nonRecoverablePropertyTaxes * _basisMultiplier,
      nonRecoverableBusiness: costRevenue.nonRecoverableBusiness * _basisMultiplier,
      nonRecoverableOthers: costRevenue.nonRecoverableOthers * _basisMultiplier,
    },
    passingRent,
    subEffectiveRent,
    subBasisMultiplier: _subBasisMultiplier,
    subRecoverable: {
      total: subTotalRecoverable,
      recoverablePropertyManagement: subCostRevenue ? subCostRevenue.recoverablePropertyManagement * _subBasisMultiplier : undefined,
      recoverableLeasingExpenses: subCostRevenue ? subCostRevenue.recoverableLeasingExpenses * _subBasisMultiplier : undefined, 
      recoverableUtilities: subCostRevenue ? subCostRevenue.recoverableUtilities * _subBasisMultiplier : undefined,
      recoverableMaintenance: subCostRevenue ? subCostRevenue.recoverableMaintenance * _subBasisMultiplier : undefined,
      recoverableInsurance: subCostRevenue ? subCostRevenue.recoverableInsurance * _subBasisMultiplier : undefined,
      recoverableJanitorial: subCostRevenue ? subCostRevenue.recoverableJanitorial * _subBasisMultiplier : undefined,
      recoverablePropertyTaxes: subCostRevenue ? subCostRevenue.recoverablePropertyTaxes * _subBasisMultiplier : undefined,
      recoverableBusiness: subCostRevenue ? subCostRevenue.recoverableBusiness * _subBasisMultiplier : undefined,
      recoverableOthers: subCostRevenue ? subCostRevenue.recoverableOthers * _subBasisMultiplier : undefined,
    },
    subNonRecoverable: {
      total: subTotalNonRecoverable,
      nonRecoverablePropertyManagement: subCostRevenue ? subCostRevenue.nonRecoverablePropertyManagement * _subBasisMultiplier : undefined,
      nonRecoverableLeasingExpenses: subCostRevenue ? subCostRevenue.nonRecoverableLeasingExpenses * _subBasisMultiplier : undefined, 
      nonRecoverableUtilities: subCostRevenue ? subCostRevenue.nonRecoverableUtilities * _subBasisMultiplier : undefined,
      nonRecoverableMaintenance: subCostRevenue ? subCostRevenue.nonRecoverableMaintenance * _subBasisMultiplier : undefined,
      nonRecoverableInsurance: subCostRevenue ? subCostRevenue.nonRecoverableInsurance * _subBasisMultiplier : undefined,
      nonRecoverableJanitorial: subCostRevenue ? subCostRevenue.nonRecoverableJanitorial * _subBasisMultiplier : undefined,
      nonRecoverablePropertyTaxes: subCostRevenue ? subCostRevenue.nonRecoverablePropertyTaxes * _subBasisMultiplier : undefined,
      nonRecoverableBusiness: subCostRevenue ? subCostRevenue.nonRecoverableBusiness * _subBasisMultiplier : undefined,
      nonRecoverableOthers: subCostRevenue ? subCostRevenue.nonRecoverableOthers * _subBasisMultiplier : undefined,
    },
    profitRent,
    subLeaseYPUntilReview: {
      value: subLeaseYPUntilReview,
      year: subMinYear
    },
    capitalValueOfProfitRentOf1stTerm,
    subLeaseReversion: _subLeaseReversion,
    profitRent2ndTerm: _profitRent2ndTerm,
    headLeaseYieldPurchase: {
      value: isNaN(yp2ndTerm) ? undefined : yp2ndTerm,
      headYear: minYearWithUE,
      subYear: subMinYearWithUE,
      year: minYearWithUE.value - subMinYearWithUE.value 
    },
    presentValue: _presentValue,
    capitalValueOfProfitRentOf2ndTerm,
    value
  }
}

function presentValue(
  allRiskYield: number,
  activeBreakOption: boolean,
  yearsToBreakOff: number,
  estimatedVoidPeriod: number,
  minYear: {key: string ,value: number},
  unexpiredLeaseTerm: Number
) {
  const ary = allRiskYield / 100
  const year = activeBreakOption
    ? {
      value: yearsToBreakOff + estimatedVoidPeriod,
      firstYear: {
        name: 'Sub lease Break option after',
        value: yearsToBreakOff
      },
      secondYear: {
        name: 'Estimated Void period',
        value: estimatedVoidPeriod
      }
    }
    : minYear.value == unexpiredLeaseTerm
      ? {
        value: minYear.value + estimatedVoidPeriod,
        firstYear: {
          name: minYear.key,
          value: minYear.value
        },
        secondYear: {
          name: 'Estimated Void period',
          value: estimatedVoidPeriod
        }
      }
      : {
        value: minYear.value,
        firstYear: {
          name: minYear.key,
          value: minYear.value
        }
      }

    const value = Math.pow(1 + ary, -year.value)
    return {
      value: rounddown4(value),
      year
    }
}

function yieldPurchase(
  allRiskYield: number,
  annualSinkingFund: number,
  taxRate: number,
  year: number,
  timingId: number
) {
  if (year === null) return undefined
  const ary = allRiskYield / 100
  const asf = annualSinkingFund / 100
  const tax = 100/(100 - taxRate)

  const denom = Math.pow(1 + asf, year) - 1

  const aryV = timingId == 1 
    ? ary
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + ary, -0.25))
      : undefined
  const asfV = timingId == 1
    ? asf
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + asf, -0.25))
      : undefined

  const temp1 = asfV / denom 
  const temp2 = temp1 * tax 
  const temp3 = aryV + temp2
  if (temp3 == 0) return undefined
  const value = 1 / temp3
  return rounddown4(value)
}

function yieldPurchase2ndTerm(
  allRiskYield: number,
  annualSinkingFund: number,
  minYear: number,
  subMinYear: number,
  taxRate: number,
  timingId: number
) {
  if (minYear == null) return undefined
  if (subMinYear == null) return undefined
  const ary = allRiskYield / 100
  const asf = annualSinkingFund / 100
  const aryV = timingId == 1 
    ? ary
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + ary, -0.25))
      : undefined
  const asfV = timingId == 1
    ? asf
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + asf, -0.25))
      : undefined
  const tax = 100 / (100 - taxRate)
  const year = minYear - subMinYear

  const denom = Math.pow(1 + asf, year) - 1
  const temp1 = asfV / denom
  const temp2 = temp1 * tax
  const temp3 = aryV + temp2
  if (temp3 == 0) return undefined
  const value = 1 / temp3
  return rounddown4(value)
}

function subLeaseReversion(
  marketRent: number,
  effectiveRent: number,
  growthRate: number,
  activeBreakOffOption: boolean,
  estimatedVoidPeriod: number,
  minYear: {
    key: string,
    value: number
  },
  unexpiredLeaseTerm: number,
  yearsToBreakOff: number,
  rentReviewTypeId: number,
  rentReviewDetailType: RentReviewDetailType,
  rentReviewDetail: number
) {
  const growthRateInVal = growthRate / 100
  const year = activeBreakOffOption
    ? {
        value: yearsToBreakOff + estimatedVoidPeriod, 
        firstYear: {
          name: 'Break option after',
          value: yearsToBreakOff
        },
        secondYear: {
          name: 'Estimated Void period',
          value: estimatedVoidPeriod
        }
      }
    : minYear.value == unexpiredLeaseTerm 
      ? {
          value: minYear.value + estimatedVoidPeriod,
          firstYear: {
            name: minYear.key,
            value: minYear.value
          },
          secondYear: {
            name: 'Estimated Void period',
            value: estimatedVoidPeriod
          }
        }
      : {
        value: minYear.value,
        firstYear: {
          name: minYear.key,
          value: minYear.value
        }
      }
  if (marketRent > effectiveRent && rentReviewTypeId == RentReviewTypes.UpwardsOnlyMarketRent) {
    return {
      value: marketRent * Math.pow((1 + growthRateInVal), year.value),
      year
    }
  }
  if (rentReviewTypeId == RentReviewTypes.UpwardsDownwardsMarketRent) {
    return {
      value: marketRent * Math.pow((1 + growthRateInVal), year.value),
      year
    }
  }
  if (rentReviewTypeId == RentReviewTypes.RPI) {
    return {
      value: effectiveRent * Math.pow((1 + growthRateInVal), year.value),
      year
    }
  }
  if (rentReviewTypeId == RentReviewTypes.CPI) {
    return {
      value: effectiveRent * Math.pow((1 + growthRateInVal), year.value),
      year
    }
  }
  if (rentReviewTypeId == RentReviewTypes.FixedRentIncrease) {
    if (rentReviewDetailType === 'percentage') {
      return {
        value:effectiveRent + (effectiveRent * rentReviewDetail / 100)
      }
    }
    if (rentReviewDetailType === 'number') {
      return {
        value: effectiveRent + rentReviewDetail
      }
    }
    return {
      value: undefined
    }
  }

  return {
    value: effectiveRent
  }
}

function profitRent2ndTerm(
  rentReviewTypeId: number,
  subLeaseReversion: number,
  passingRent: number,
  subLeaseTotalRecoverable: number,
  subLeaseTotalNonRecoverable: number,
  subLeaseEffectiveRent: number
): {value: number} {
  if (
    rentReviewTypeId === RentReviewTypes.FixedRentIncrease ||
    rentReviewTypeId === RentReviewTypes.CPI ||
    rentReviewTypeId === RentReviewTypes.RPI ||
    (rentReviewTypeId === RentReviewTypes.UpwardsOnlyMarketRent && subLeaseReversion === subLeaseEffectiveRent) ||
    rentReviewTypeId === undefined
  ) {
    return {value: subLeaseReversion - passingRent + subLeaseTotalRecoverable - subLeaseTotalNonRecoverable}
  }
  return {
    value: subLeaseReversion - passingRent
  }
}